import React, { useState } from 'react';
import { Bell, X, Wrench, CheckCircle } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { ScrollArea } from './scroll-area';
import { Progress } from './progress';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotifications, MaintenanceNotification } from '@/contexts/NotificationContext';

const NotificationCenter: React.FC = () => {
  const { 
    notifications, 
    completedNotifications,
    markAsRead, 
    dismissCompletedNotification,
    unreadCount 
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const handleNotificationClick = (id: string) => {
    markAsRead(id);
  };

  const handleDismissCompletedNotification = (id: string) => {
    dismissCompletedNotification(id);
  };

  // Calcular el tiempo restante estimado para una notificación
  const calculateTimeRemaining = (notification: MaintenanceNotification) => {
    const createdAt = parseISO(notification.createdAt);
    const estimatedEndTime = new Date(createdAt);
    estimatedEndTime.setHours(estimatedEndTime.getHours() + notification.estimatedTimeHours);
    
    const now = new Date();
    const totalDuration = estimatedEndTime.getTime() - createdAt.getTime();
    const elapsed = now.getTime() - createdAt.getTime();
    
    // Calcular el porcentaje de tiempo transcurrido (invertido para mostrar tiempo restante)
    const percentElapsed = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    const percentRemaining = Math.max(0, 100 - percentElapsed);
    
    return percentRemaining;
  };

  // Determinar el color de la barra de progreso según el tiempo restante
  const getProgressColor = (timeRemaining: number) => {
    if (timeRemaining > 66) return 'bg-green-500';
    if (timeRemaining > 33) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Combinar todas las notificaciones
  const allNotifications = [...notifications, ...completedNotifications];
  const totalCount = unreadCount + completedNotifications.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative inline-block cursor-pointer p-1">
          <div className="w-10 h-10 rounded-full bg-[rgb(30,58,74)] flex items-center justify-center shadow-md hover:bg-[rgb(25,50,65)] transition-colors">
            <Bell className="h-5 w-5 text-white" />
          </div>
          {totalCount > 0 && (
            <div className="absolute -top-1 -right-1 bg-[hsl(45,100%,50%)] text-white rounded-full min-w-[20px] h-5 flex items-center justify-center text-xs font-bold px-1 shadow-sm">
              {totalCount > 99 ? '99+' : totalCount}
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Notificaciones</h3>
          </div>
        </div>
        
        <ScrollArea className="h-96">
          {allNotifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No hay notificaciones
            </div>
          ) : (
            <div className="divide-y">
              {allNotifications.map((notification) => {
                const isCompleted = notification.completed;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-muted/50 ${
                      !notification.read && !isCompleted ? 'bg-blue-50' : 
                      isCompleted ? 'bg-green-50' : ''
                    }`}
                    onClick={() => !isCompleted && handleNotificationClick(notification.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${
                        isCompleted ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                      }`}>
                        {isCompleted ? <CheckCircle className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-base">
                            {notification.screenName}
                          </p>
                          {isCompleted ? (
                            <div 
                              className="h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDismissCompletedNotification(notification.id);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </div>
                          ) : !notification.read ? (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          ) : null}
                        </div>
                        
                        <p className={`text-sm mb-2 ${
                          isCompleted ? 'text-green-600' : 'text-muted-foreground'
                        }`}>
                          {isCompleted ? 'Mantenimiento completado' : 'Pantalla en mantenimiento'}
                        </p>
                        
                        {!isCompleted && (
                          <div className="mb-2">
                            <div className="text-xs text-muted-foreground mb-1 flex justify-between">
                              <span>Tiempo restante estimado ({notification.estimatedTimeHours} horas):</span>
                              <span>{Math.round(calculateTimeRemaining(notification))}%</span>
                            </div>
                            <Progress 
                              value={calculateTimeRemaining(notification)} 
                              className={getProgressColor(calculateTimeRemaining(notification))} 
                            />
                          </div>
                        )}
                        
                        {notification.screenLocation && (
                          <p className="text-xs text-muted-foreground">
                            Ubicación: {notification.screenLocation}
                          </p>
                        )}
                        
                        <p className="text-xs text-muted-foreground mt-1">
                          {isCompleted ? (
                            <>Completado {formatDistanceToNow(parseISO(notification.completedAt || notification.createdAt), { addSuffix: true, locale: es })}</>
                          ) : (
                            <>Reportado {formatDistanceToNow(parseISO(notification.createdAt), { addSuffix: true, locale: es })}</>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter; 